import { useState } from 'react';
import { MessageCircle, Send, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { MessageBubble } from './conversation/MessageBubble';
import { useMessagesQuery, useSendMessageMutation } from '../queries/messages';
import { PortalSpinner } from './PortalSpinner';
import { PORTAL_ROUTES, PORTAL_RESOURCE_TYPES } from '../constants';
import { useConversationSocket } from '../hooks/useConversationSocket';
import { useMessageScroll } from '../hooks/useMessageScroll';

export type LatestConversation = {
  encodedId: string;
  title: string;
  state: 'open' | 'closed';
  lastMessage?: { content: string; fromPatient: boolean; time: string };
};

export const ChatPopup = ({
  conversation,
  onClose,
}: {
  conversation: LatestConversation;
  onClose: () => void;
}) => {
  const [text, setText] = useState('');

  useConversationSocket(conversation.encodedId);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useMessagesQuery(conversation.encodedId);
  const { mutate: sendMessage, isPending: isSending } = useSendMessageMutation(conversation.encodedId);
  const messages = [...(data?.pages.flatMap((p) => p.items) ?? [])].reverse();

  const { bottomRef, setScrollRef, sentinelRef } = useMessageScroll({
    pagesLength: data?.pages.length ?? 0,
    messagesLength: messages.length,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
  });

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;
    sendMessage(trimmed, { onSuccess: () => setText('') });
  };

  return createPortal(
    <div className="fixed z-[9999] flex flex-col bg-white rounded-2xl shadow-2xl shadow-black/20 border border-gray-200 overflow-hidden bottom-3 left-3 right-3 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[360px] h-[520px] max-h-[calc(100vh-80px)]">
      <div className="flex items-center justify-between px-4 py-3 bg-teamcare-blue flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle size={15} className="text-white/80 flex-shrink-0" />
          <span className="text-sm font-semibold text-white truncate">{conversation.title}</span>
          <span
            className={`inline-flex items-center flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
              conversation.state === 'open' ? 'bg-white/20 text-white' : 'bg-white/10 text-white/60'
            }`}
          >
            {conversation.state}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Link
            to={`${PORTAL_ROUTES.ROOT}/${PORTAL_RESOURCE_TYPES.CONVERSATIONS}/${conversation.encodedId}`}
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors"
          >
            <ExternalLink size={13} />
          </Link>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div ref={setScrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50/60">
        {isLoading ? (
          <PortalSpinner padding="py-10" />
        ) : !messages.length ? (
          <p className="text-xs text-secondary-400 text-center pt-16">No messages yet</p>
        ) : (
          <>
            {(hasNextPage || isFetchingNextPage) && (
              <div ref={sentinelRef} className="flex justify-center py-1">
                <PortalSpinner />
              </div>
            )}
            {messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {conversation.state === 'closed' ? (
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex items-center justify-center gap-2 text-sm text-secondary-500 bg-gray-50">
          <span className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          This conversation has been resolved.
        </div>
      ) : (
        <div className="border-t border-gray-100 px-3 py-2.5 bg-white flex-shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              disabled={isSending}
              placeholder="Type a message… (Enter to send)"
              className="flex-1 resize-none text-sm text-secondary-800 placeholder:text-secondary-400 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-teamcare-blue focus:ring-1 focus:ring-teamcare-blue/20 disabled:opacity-50 transition-colors"
            />
            <button
              onClick={handleSend}
              disabled={!text.trim() || isSending}
              className="flex items-center justify-center w-9 h-9 rounded-xl bg-teamcare-blue text-white hover:bg-teamcare-blue-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0 mb-0.5"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};
